import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
   Box,
   Container,
   Paper,
   Typography,
   Avatar,
   Button,
   Card,
   CardContent,
   CircularProgress,
   Alert,
   useTheme,
   useMediaQuery,
   Chip,
   Snackbar,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   IconButton,
} from '@mui/material';
import {
   Person as PersonIcon,
   QrCode as QrCodeIcon,
   PersonAdd as PersonAddIcon,
   Email as EmailIcon,
   Phone as PhoneIcon,
   LocationOn as LocationIcon,
   Language as LanguageIcon,
   Work as WorkIcon,
   CheckCircle as CheckCircleIcon,
   Psychology as PsychologyIcon,
   Search as SearchIcon,
   Star as StarIcon,
   Download as DownloadIcon,
   Business as BusinessIcon,
   FileDownload as FileDownloadIcon,
   Close as CloseIcon,
   HourglassEmpty as HourglassEmptyIcon,
} from '@mui/icons-material';
import {
   doc,
   getDoc,
   updateDoc,
   increment,
   addDoc,
   collection,
   serverTimestamp,
   query,
   where,
   getDocs,
   deleteDoc,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useUser } from '../../contexts/UserContext';
import { useWebSocketChat } from '../../contexts/WebSocketChatContext';
import { useFloatingChat } from '../../contexts/FloatingChatContext';
import { generateFingerprint } from '../../utils/browserFingerprint';
import SignupModal from '../../components/auth/SignupModal';
import { sendNetworkRequest } from '../../services/networkService';

// Using require syntax to avoid ES module import issues
const QRCode = require('qrcode.react').QRCodeSVG;

interface UserProfile {
   uid: string;
   displayName: string;
   email?: string;
   photoURL?: string;
   bio?: string;
   title?: string;
   companyName?: string;
   phoneNumber?: string;
   location?: string;
   website?: string;
   tagline?: string;
   whatIDo?: Array<{
      id: string;
      emoji: string;
      title: string;
      subtitle: string;
      color: string;
      description: string;
   }>;
   interests?: Array<{
      id: string;
      emoji: string;
      name: string;
      selected: boolean;
      isCustom?: boolean;
   }>;
   lookingFor?: Array<{
      id: string;
      emoji: string;
      title: string;
      description: string;
   }>;
   featuredProjects?: Array<{
      id: string;
      title: string;
      description: string;
   }>;
}

const QRCodeShare: React.FC = () => {
   const { userId } = useParams<{ userId: string }>();
   const navigate = useNavigate();
   const { currentUser } = useUser();
   const { createOneOnOneChat } = useWebSocketChat();
   const { openFloatingChat } = useFloatingChat();
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

   const [profile, setProfile] = useState<UserProfile | null>(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [scanRecorded, setScanRecorded] = useState(false);
   const [downloading, setDownloading] = useState<string | null>(null);
   const [signupModalOpen, setSignupModalOpen] = useState(false);
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('success');
   const [isInNetwork, setIsInNetwork] = useState(false);
   const [checkingNetwork, setCheckingNetwork] = useState(true);
   const [showNetworkModal, setShowNetworkModal] = useState(false);
   const [relationshipType, setRelationshipType] = useState<string>('');
   const [relationshipLabel, setRelationshipLabel] = useState('');
   const [message, setMessage] = useState('');
   const [sendingRequest, setSendingRequest] = useState(false);
   const [hasPendingRequest, setHasPendingRequest] = useState(false);
   const [pendingRequestDirection, setPendingRequestDirection] = useState<'sent' | 'received' | null>(null);
   const [refreshNetworkStatus, setRefreshNetworkStatus] = useState(0);
   const [startingChat, setStartingChat] = useState(false);

   // Capture source from URL parameter (business_card, profile_qr, or profile_link)
   const searchParams = new URLSearchParams(window.location.search);
   const source = searchParams.get('source') || 'profile_qr'; // Default to 'profile_qr' for QR scans

   console.log('🔗 QR Page URL params:', {
      userId: userId, // This is the referrer's ID
      source,
      fullURL: window.location.href,
   });

   const publicProfileUrl = `${window.location.origin}/profile/public/${userId}?ref=qr`;

   useEffect(() => {
      const loadProfile = async () => {
         if (!userId) return;

         try {
            console.log('📱 Loading profile from API for userId:', userId);
            
            // Use the public API endpoint instead of direct Firestore queries
            // This works consistently across all devices including mobile
            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/profile/public/${userId}`
            );

            if (!response.ok) {
               if (response.status === 404) {
                  setError('Profile not found');
               } else {
                  throw new Error(`Failed to load profile: ${response.status}`);
               }
               return;
            }

            const profileData = await response.json();
            console.log('✅ Profile loaded successfully:', profileData);

            const mappedLookingFor = (profileData.lookingFor || []).map((item: any) => ({
               ...item,
               description: item.description || '',
            }));

            const mappedFeaturedProjects = (profileData.featuredProjects || []).map(
               (item: any) => ({
                  ...item,
                  description: item.description || '',
               })
            );

            console.log('Looking For data:', mappedLookingFor);
            console.log('Featured Projects data:', mappedFeaturedProjects);

            setProfile({
               uid: userId,
               displayName: profileData.displayName || 'Unknown User',
               email: profileData.email,
               photoURL: profileData.photoURL,
               bio: profileData.bio,
               title: profileData.title,
               companyName: profileData.companyName,
               phoneNumber: profileData.phoneNumber,
               location: profileData.location,
               website: profileData.website,
               tagline: profileData.tagline,
               whatIDo: (profileData.whatIDo || []).map((item: any) => ({
                  ...item,
                  description: item.description || '',
               })),
               interests: profileData.interests || [],
               lookingFor: mappedLookingFor,
               featuredProjects: mappedFeaturedProjects,
            });

            // Record scan (for both logged-in and anonymous users)
            // Skip if user is viewing their own profile OR if already recorded in this session
            const sessionKey = `scan_recorded_${userId}_${source}`;
            const alreadyRecordedInSession = sessionStorage.getItem(sessionKey);

            if (!scanRecorded && !alreadyRecordedInSession && userId) {
               if (currentUser && currentUser.uid === userId) {
                  // User viewing their own profile, don't record
                  console.log('User viewing own profile, skipping scan recording');
               } else {
                  // Record scan for logged-in users or anonymous with source
                  await recordQRScan(userId, currentUser, source);
                  setScanRecorded(true);
                  // Mark as recorded in this session to prevent duplicate scans on refresh
                  sessionStorage.setItem(sessionKey, 'true');
                  console.log('✅ Scan recorded and marked in session storage');
               }
            } else if (alreadyRecordedInSession) {
               console.log('ℹ️ Scan already recorded in this session, skipping duplicate');
            }
         } catch (err) {
            console.error('❌ Error loading profile:', err);
            setError('Failed to load profile');
         } finally {
            setLoading(false);
         }
      };

      loadProfile();
   }, [userId, currentUser, scanRecorded, source]);

   // Check if profile user is in current user's network
   useEffect(() => {
      const checkNetworkStatus = async () => {
         if (!currentUser || !userId) {
            setCheckingNetwork(false);
            setIsInNetwork(false);
            setHasPendingRequest(false);
            return;
         }

         if (currentUser.uid === userId) {
            setCheckingNetwork(false);
            setIsInNetwork(false);
            setHasPendingRequest(false);
            return;
         }

         try {
            setCheckingNetwork(true);
            console.log('🔍 Starting network status check for:', userId);
            
            // Get the profile email first
            let profileEmail = profile?.email;
            if (!profileEmail) {
               // Try to get email from user document
               try {
                  const userDoc = await getDoc(doc(db, 'users', userId));
                  if (userDoc.exists()) {
                     profileEmail = userDoc.data()?.email;
                  }
               } catch (e) {
                  console.warn('Could not fetch user email:', e);
               }
            }
            
            console.log('👤 Profile email:', profileEmail);
            console.log('👤 Current user email:', currentUser.email);
            
            // Check relationship_network collection for accepted relationships
            const relationshipNetworkRef = collection(db, 'relationship_network');
            
            // Get all relationships for current user
            const myRelationshipsQuery = query(
               relationshipNetworkRef,
               where('userId', '==', currentUser.uid),
               where('status', '==', 'accepted')
            );
            
            // Get all relationships where current user is the target
            const theirRelationshipsQuery = query(
               relationshipNetworkRef,
               where('relationToUserId', '==', currentUser.uid),
               where('status', '==', 'accepted')
            );
            
            const [myRelationships, theirRelationships] = await Promise.all([
               getDocs(myRelationshipsQuery),
               getDocs(theirRelationshipsQuery)
            ]);
            
            console.log('📊 Found accepted relationships:', {
               myRelationships: myRelationships.size,
               theirRelationships: theirRelationships.size
            });
            
            // Check if profile user is in the relationships (by userId OR email)
            let isConnected = false;
            
            // Check my relationships
            myRelationships.forEach(doc => {
               const data = doc.data();
               const matchByUserId = data.relationToUserId === userId;
               const matchByEmail = profileEmail && data.relationToEmail && 
                                   data.relationToEmail.toLowerCase() === profileEmail.toLowerCase();
               
               console.log('🔍 Checking my relationship:', {
                  docId: doc.id,
                  relationToUserId: data.relationToUserId,
                  targetUserId: userId,
                  relationToEmail: data.relationToEmail,
                  targetEmail: profileEmail,
                  matchByUserId,
                  matchByEmail
               });
               
               if (matchByUserId || matchByEmail) {
                  isConnected = true;
                  console.log('✅ Found connection in my relationships:', { id: doc.id, matchByUserId, matchByEmail });
               }
            });
            
            // Check their relationships (where I am the target)
            if (!isConnected) {
               theirRelationships.forEach(doc => {
                  const data = doc.data();
                  const matchByUserId = data.userId === userId;
                  const matchByEmail = profileEmail && data.email && 
                                      data.email.toLowerCase() === profileEmail.toLowerCase();
                  
                  console.log('🔍 Checking their relationship:', {
                     docId: doc.id,
                     theirUserId: data.userId,
                     targetUserId: userId,
                     theirEmail: data.email,
                     targetEmail: profileEmail,
                     matchByUserId,
                     matchByEmail
                  });
                  
                  if (matchByUserId || matchByEmail) {
                     isConnected = true;
                     console.log('✅ Found connection in their relationships:', { id: doc.id, matchByUserId, matchByEmail });
                  }
               });
            }
            
            console.log('📊 Network connection status:', isConnected);
            
            // Check for PENDING relationships in the relationship_network collection
            // This is where pending connections are stored
            
            // Check pending relationships I created (from me to them)
            const myPendingRelationshipsQuery = query(
               relationshipNetworkRef,
               where('userId', '==', currentUser.uid),
               where('status', '==', 'pending'),
               where('relationToUserId', '==', userId)
            );
            
            // Check pending relationships they created (from them to me)
            const theirPendingRelationshipsQuery = query(
               relationshipNetworkRef,
               where('userId', '==', userId),
               where('status', '==', 'pending'),
               where('relationToUserId', '==', currentUser.uid)
            );
            
            const [mySentSnapshot, theirSentSnapshot] = await Promise.all([
               getDocs(myPendingRelationshipsQuery),
               getDocs(theirPendingRelationshipsQuery)
            ]);
            
            console.log('📨 Pending relationships snapshot sizes:', {
               mySent: mySentSnapshot.size,
               theirSent: theirSentSnapshot.size
            });
            
            // Log ALL pending relationships for debugging
            console.log('📝 ALL MY PENDING RELATIONSHIPS:');
            mySentSnapshot.forEach(doc => {
               const data = doc.data();
               console.log('  -', doc.id, ':', {
                  userId: data.userId,
                  relationToUserId: data.relationToUserId,
                  displayName: data.displayName,
                  status: data.status
               });
            });
            
            console.log('📝 ALL THEIR PENDING RELATIONSHIPS:');
            theirSentSnapshot.forEach(doc => {
               const data = doc.data();
               console.log('  -', doc.id, ':', {
                  userId: data.userId,
                  relationToUserId: data.relationToUserId,
                  displayName: data.displayName,
                  status: data.status
               });
            });
            
            // Check if any pending or rejected relationships exist
            let hasPending = false;
            let hasRejected = false;
            let direction: 'sent' | 'received' | null = null;
            const matchingRequests: any[] = [];
            
            // Check pending relationships I created (I am userId, they are relationToUserId)
            if (!mySentSnapshot.empty) {
               mySentSnapshot.forEach(doc => {
                  const data = doc.data();
                  console.log('✅ FOUND PENDING RELATIONSHIP I CREATED:', {
                     id: doc.id,
                     from: data.userId,
                     to: data.relationToUserId,
                     status: data.status
                  });
                  matchingRequests.push({ id: doc.id, direction: 'sent', ...data });
                  hasPending = true;
                  direction = 'sent';
               });
            }
            
            // Check pending relationships they created (they are userId, I am relationToUserId)
            if (!theirSentSnapshot.empty) {
               theirSentSnapshot.forEach(doc => {
                  const data = doc.data();
                  console.log('✅ FOUND PENDING RELATIONSHIP THEY CREATED:', {
                     id: doc.id,
                     from: data.userId,
                     to: data.relationToUserId,
                     status: data.status
                  });
                  matchingRequests.push({ id: doc.id, direction: 'received', ...data });
                  hasPending = true;
                  if (!direction) direction = 'received';
               });
            }
            
            // Also check for rejected relationships (to allow retry)
            const rejectedQuery = query(
               relationshipNetworkRef,
               where('userId', '==', currentUser.uid),
               where('status', '==', 'rejected'),
               where('relationToUserId', '==', userId)
            );
            const rejectedSnapshot = await getDocs(rejectedQuery);
            
            if (!rejectedSnapshot.empty) {
               console.log('ℹ️ Found rejected relationship - will show Send Network Request button');
               hasRejected = true;
            }
            
            console.log('✅ Total matching pending relationships:', matchingRequests.length, matchingRequests);
            
            setIsInNetwork(isConnected);
            // Only set hasPendingRequest if there's a pending request AND it's not rejected
            setHasPendingRequest(hasPending && !hasRejected);
            setPendingRequestDirection(direction);
            
            console.log('🎯 Final state:', {
               currentUserId: currentUser.uid,
               profileUserId: userId,
               profileEmail: profileEmail,
               isInNetwork: isConnected,
               hasPendingRequest: hasPending && !hasRejected,
               pendingRequestDirection: direction,
               willShowStatus: hasPending && !hasRejected,
               buttonText: isConnected ? 'Start Conversation' : 'Send Network Request'
            });
         } catch (error) {
            console.error('❌ Error checking network status:', error);
            setIsInNetwork(false);
            setHasPendingRequest(false);
         } finally {
            setCheckingNetwork(false);
         }
      };

      checkNetworkStatus();
   }, [currentUser, userId, profile?.email, refreshNetworkStatus]);

   const recordQRScan = async (profileUserId: string, scanner: any, scanSource: string) => {
      try {
         // Generate hardware-only fingerprint for most stable tracking
         // Hardware fingerprint is based on CPU, GPU, RAM, screen, audio - very hard to change
         const fingerprint = await generateFingerprint(true); // true = hardware-only mode
         console.log('Generated hardware fingerprint:', fingerprint);
         console.log('Scan source:', scanSource);

         // Prepare scanner info
         const scannerInfo = scanner
            ? {
                 scannedBy: scanner.uid,
                 scannerName: scanner.displayName || 'Unknown User',
                 scannerEmail: scanner.email || null,
                 scannerPhotoURL: scanner.photoURL || null,
                 userAgent: navigator.userAgent,
              }
            : {
                 scannedBy: null,
                 scannerName: 'Anonymous',
                 scannerEmail: null,
                 scannerPhotoURL: null,
                 userAgent: navigator.userAgent,
              };

         // Record scan via API
         const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4001'}/api/profile/${profileUserId}/record-scan`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               fingerprint,
               scannerInfo,
               source: scanSource, // Include source (qr or link)
            }),
         });

         if (!response.ok) {
            throw new Error('Failed to record scan');
         }

         const result = await response.json();
         console.log('QR scan recorded successfully:', result);

         const sourceLabels: { [key: string]: string } = {
            business_card: 'Business Card QR',
            profile_qr: 'Profile QR Code',
            profile_link: 'Profile Link',
         };
         const sourceLabel = sourceLabels[scanSource] || 'Profile Link';

         if (result.isUnique) {
            console.log(`✅ This is a unique ${sourceLabel} scan! (First time from this device)`);
         } else {
            console.log(`🔄 This is a repeat ${sourceLabel} scan from a known device.`);
         }
      } catch (error) {
         console.error('Error recording scan:', error);
      }
   };

   const handleAddToContact = async () => {
      console.log('🔘 Button clicked - Current state:', {
         currentUser: currentUser?.uid,
         userId,
         isInNetwork,
         hasPendingRequest,
         checkingNetwork
      });

      if (!currentUser) {
         // Show signup modal instead of redirecting
         setSignupModalOpen(true);
         return;
      }

      if (currentUser.uid === userId) {
         // If it's the profile owner, redirect to their profile
         navigate('/profile');
         return;
      }

      // Check if user is in network
      if (!isInNetwork) {
         // Check if there's already a pending request
         if (hasPendingRequest) {
            console.log('❌ User already has a pending network request');
            setSnackbarMessage('You already have a pending network request with this user. Please wait for them to respond.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
         }
         
         // Not in network and no pending request - show network request modal
         console.log('✅ User not in network, showing network request modal');
         setShowNetworkModal(true);
         return;
      }

      // User is in network - create chat and open floating chat with specific user
      setStartingChat(true);
      try {
         if (userId && profile) {
            console.log('🔄 Starting chat creation...', { 
               userId, 
               profileName: profile.displayName,
               currentUserId: currentUser.uid 
            });
            
            let chatRoom = null;
            let retryCount = 0;
            const maxRetries = 2;
            
            // Try to create/get chat room with retries
            while (retryCount < maxRetries && !chatRoom) {
               try {
                  chatRoom = await createOneOnOneChat(userId);
                  if (chatRoom) break;
               } catch (err: any) {
                  retryCount++;
                  console.log(`🔄 Retry ${retryCount}/${maxRetries} for chat creation...`);
                  if (retryCount >= maxRetries) throw err;
                  // Wait a bit before retrying
                  await new Promise(resolve => setTimeout(resolve, 1000));
               }
            }
            
            console.log('📨 Chat creation result:', { 
               success: !!chatRoom, 
               chatRoom,
               retries: retryCount
            });
            
            if (chatRoom) {
               console.log('✅ Chat created successfully, opening floating chat...');
               
               // Show success message
               setSnackbarMessage('Opening conversation...');
               setSnackbarSeverity('success');
               setSnackbarOpen(true);
               
               // Open the floating chat first
               try {
                  console.log('🔄 Opening floating chat with:', chatRoom);
                  await openFloatingChat(chatRoom);
                  console.log('✅ Floating chat opened');
               } catch (floatingError) {
                  console.warn('⚠️ Could not open floating chat, but will navigate:', floatingError);
               }
               
               // Then navigate to dashboard with state indicating chat was opened
               console.log('🔄 Navigating to dashboard with chat state...');
               navigate('/dashboard', { 
                  state: { 
                     openChat: true,
                     chatRoomId: chatRoom.id,
                     userId: userId 
                  } 
               });
            } else {
               console.error('❌ createOneOnOneChat returned null/undefined after retries');
               // Still navigate to dashboard - the chat might be there
               setSnackbarMessage('Opening dashboard... Your conversation might be available there.');
               setSnackbarSeverity('info');
               setSnackbarOpen(true);
               
               setTimeout(() => {
                  navigate('/dashboard', { 
                     state: { 
                        openChat: true,
                        userId: userId 
                     } 
                  });
               }, 1000);
            }
         } else {
            console.error('❌ Missing userId or profile:', { userId, profile: !!profile });
            setSnackbarMessage('Failed to start conversation. Profile information missing.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         }
      } catch (error: any) {
         console.error('❌ Error starting chat:', {
            error,
            message: error?.message,
            stack: error?.stack
         });
         
         // Check if it's a timeout error - navigate to dashboard anyway
         if (error?.message?.includes('timeout') || error?.message?.includes('Timeout')) {
            setSnackbarMessage('Taking longer than expected. Redirecting to dashboard...');
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
            setTimeout(() => {
               navigate('/dashboard', { 
                  state: { 
                     openChat: true,
                     userId: userId 
                  } 
               });
            }, 1000);
         } else {
            setSnackbarMessage(`Failed to start conversation: ${error?.message || 'Unknown error'}`);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         }
      } finally {
         setStartingChat(false);
      }
   };

   const handleNetworkRequestSent = async () => {
      if (!currentUser || !profile) return;

      if (!relationshipType) {
         setSnackbarMessage('Please select a relationship type');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setSendingRequest(true);
      try {
         // First, delete any existing rejected relationships to allow fresh retry
         const rejectedQuery = query(
            collection(db, 'relationship_network'),
            where('userId', '==', currentUser.uid),
            where('relationToUserId', '==', userId),
            where('status', '==', 'rejected')
         );
         const rejectedSnapshot = await getDocs(rejectedQuery);
         
         // Delete all rejected relationships
         const deletePromises = rejectedSnapshot.docs.map(docSnap => 
            deleteDoc(docSnap.ref)
         );
         await Promise.all(deletePromises);
         
         if (!rejectedSnapshot.empty) {
            console.log('🗑️ Deleted', rejectedSnapshot.size, 'rejected relationship(s)');
         }

         // Create pending relationship in relationship_network collection
         const relationshipData = {
            userId: currentUser.uid,
            relationToUserId: userId || '',
            displayName: profile.displayName || 'User',
            email: profile.email || '',
            photoURL: profile.photoURL || null,
            notes: message || '',
            relationType: relationshipType,
            relationshipLabel: relationshipLabel || relationshipType,
            source: 'profile_qr',
            sourceDetail: 'Sent network request from profile page',
            status: 'pending',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
         };

         await addDoc(collection(db, 'relationship_network'), relationshipData);
         
         console.log('✅ Pending relationship created in relationship_network');

         setShowNetworkModal(false);
         setSnackbarMessage('Network request sent successfully!');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
         
         // Reset form
         setRelationshipType('');
         setRelationshipLabel('');
         setMessage('');
         
         // Refresh network status to update the button
         setRefreshNetworkStatus(prev => prev + 1);
      } catch (error: any) {
         console.error('Error sending network request:', error);
         setSnackbarMessage(error.message || 'Failed to send network request');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSendingRequest(false);
      }
   };

   const handleSignupSuccess = async (user: any) => {
      console.log('✅ Signup successful for user:', user);
      console.log('📋 User info:', {
         uid: user.uid,
         email: user.email,
         displayName: user.displayName,
      });

      // Check if contact waiting was created
      if (user.contactWaitingCreated !== undefined) {
         if (user.contactWaitingCreated) {
            console.log('✅ User added to contact waiting list for referrer:', userId);
         } else {
            console.warn('⚠️ User was NOT added to contact waiting list');
         }
      }

      // Check if network request was created
      if (user.networkRequestCreated !== undefined) {
         if (user.networkRequestCreated) {
            console.log('✅ Network request created in networkRequests collection');
         } else {
            console.warn('⚠️ Network request was NOT created');
         }
      }

      // Check if signup was tracked permanently
      if (user.signupTracked !== undefined) {
         if (user.signupTracked) {
            console.log('✅ Signup tracked permanently in user_qr_codes collection');
         } else {
            console.warn('⚠️ Signup was NOT tracked permanently');
         }
      }

      // Refresh the page to reflect the logged-in state
      window.location.reload();
   };

   const handleDownloadBusinessCard = async () => {
      if (!userId) return;

      setDownloading('business-card');
      try {
         const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4001'}/api/profile/${userId}/business-card`, {
            method: 'GET',
            headers: {
               Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
            },
         });

         if (!response.ok) {
            throw new Error('Failed to download business card');
         }

         const blob = await response.blob();
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = `${profile?.displayName || 'Business Card'}.pdf`;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
      } catch (error) {
         console.error('Error downloading business card:', error);
         setError('Failed to download business card');
      } finally {
         setDownloading(null);
      }
   };

   const handleDownloadProfile = async () => {
      if (!userId) return;

      setDownloading('profile');
      try {
         const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4001'}/api/profile/${userId}/download`, {
            method: 'GET',
            headers: {
               Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
            },
         });

         if (!response.ok) {
            throw new Error('Failed to download profile');
         }

         const blob = await response.blob();
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = `${profile?.displayName || 'Profile'}_Profile.pdf`;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
      } catch (error) {
         console.error('Error downloading profile:', error);
         setError('Failed to download profile');
      } finally {
         setDownloading(null);
      }
   };

   if (loading) {
      return (
         <Container maxWidth='sm' sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
               <CircularProgress />
            </Box>
         </Container>
      );
   }

   if (error || !profile) {
      return (
         <Container maxWidth='sm' sx={{ py: 4 }}>
            <Alert severity='error'>{error || 'Profile not found'}</Alert>
         </Container>
      );
   }

   return (
      <Box
         sx={{
            minHeight: '100vh',
            padding: 0,
         }}
      >
         <Container maxWidth='sm'>
            <Paper
               sx={{
                  borderRadius: 0,
                  overflow: 'hidden',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                  backgroundColor: 'white',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  backdropFilter: 'blur(10px)',
               }}
            >
               {/* Header Section with Primary Color Background */}
               <Box
                  sx={{
                     background: 'linear-gradient(135deg, #00C4A7 0%, #017a7f 100%)',
                     padding: 4,
                     textAlign: 'center',
                     position: 'relative',
                  }}
               >
                  {/* Profile Photo with Verified Badge */}
                  <Box
                     sx={{
                        position: 'relative',
                        display: 'inline-block',
                        mb: 2,
                     }}
                  >
                     <Avatar
                        src={profile.photoURL}
                        sx={{
                           width: 160,
                           height: 160,
                           border: '6px solid white',
                           boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                           fontSize: 64,
                           fontWeight: 'bold',
                           background: profile.photoURL
                              ? 'transparent'
                              : 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                           color: 'white',
                        }}
                     >
                        {!profile.photoURL && profile.displayName?.charAt(0)}
                     </Avatar>

                     {/* Verified Badge */}
                     <Box
                        sx={{
                           position: 'absolute',
                           bottom: 8,
                           right: 8,
                           background: '#10b981',
                           borderRadius: '50%',
                           width: 36,
                           height: 36,
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           border: '4px solid white',
                           boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        }}
                     >
                        <CheckCircleIcon sx={{ color: 'white', fontSize: 18 }} />
                     </Box>
                  </Box>

                  {/* Name and Tagline */}
                  <Typography
                     variant='h4'
                     sx={{
                        color: 'white',
                        fontWeight: 700,
                        mb: 1,
                        fontSize: isMobile ? '1.75rem' : '2rem',
                     }}
                  >
                     {profile.displayName}
                  </Typography>

                  <Typography
                     sx={{
                        color: 'white',
                        opacity: 0.95,
                        lineHeight: 1.5,
                        mb: 2,
                        fontSize: isMobile ? '0.9rem' : '1rem',
                     }}
                  >
                     {profile.tagline || 'Building connections through technology'}
                  </Typography>

                  {/* Location and Company Badges */}
                  <Box
                     sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        mb: 2,
                     }}
                  >
                     {profile.location && (
                        <Chip
                           icon={<LocationIcon sx={{ fontSize: 16 }} />}
                           label={profile.location}
                           sx={{
                              background: 'rgba(255, 255, 255, 0.2)',
                              backdropFilter: 'blur(10px)',
                              color: 'white',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              '& .MuiChip-icon': {
                                 color: 'white',
                              },
                           }}
                        />
                     )}
                     {profile.companyName && (
                        <Chip
                           icon={<WorkIcon sx={{ fontSize: 16 }} />}
                           label={profile.companyName}
                           sx={{
                              background: 'rgba(255, 255, 255, 0.2)',
                              backdropFilter: 'blur(10px)',
                              color: 'white',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              '& .MuiChip-icon': {
                                 color: 'white',
                              },
                           }}
                        />
                     )}
                  </Box>

                  {/* Connect Button or Status */}
                  {checkingNetwork ? (
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                        <CircularProgress size={24} sx={{ color: 'white' }} />
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                           Checking connection status...
                        </Typography>
                     </Box>
                  ) : !currentUser ? (
                  <Button
                     variant='contained'
                     size='large'
                     onClick={handleAddToContact}
                     sx={{
                        background: 'white',
                        color: '#00C4A7',
                        borderRadius: 3,
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        textTransform: 'none',
                        '&:hover': {
                           background: 'rgba(255, 255, 255, 0.95)',
                           transform: 'translateY(-2px)',
                           boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
                        },
                     }}
                  >
                        Connect on WeTrippen
                  </Button>
                  ) : currentUser.uid === profile?.uid ? (
                     <Button
                        variant='contained'
                        size='large'
                        onClick={handleAddToContact}
                        sx={{
                           background: 'white',
                           color: '#00C4A7',
                           borderRadius: 3,
                           px: 4,
                           py: 1.5,
                           fontSize: '1rem',
                           fontWeight: 600,
                           boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                           textTransform: 'none',
                           '&:hover': {
                              background: 'rgba(255, 255, 255, 0.95)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
                           },
                        }}
                     >
                        Edit Profile
                     </Button>
                  ) : hasPendingRequest ? (
                     <Chip
                        icon={<HourglassEmptyIcon />}
                        label={
                           pendingRequestDirection === 'sent'
                              ? 'Waiting for Connection Approval'
                              : 'Pending Connection Request'
                        }
                        sx={{
                           background: 'rgba(255, 255, 255, 0.95)',
                           color: '#00C4A7',
                           border: '2px solid #00C4A7',
                           borderRadius: 3,
                           px: 2,
                           py: 2.5,
                           fontSize: '1rem',
                           fontWeight: 600,
                           boxShadow: '0 4px 12px rgba(0, 196, 167, 0.25)',
                           '& .MuiChip-icon': {
                              color: '#00C4A7',
                              fontSize: '1.2rem',
                           },
                           '& .MuiChip-label': {
                              px: 1,
                           },
                        }}
                     />
                  ) : (
                     <Button
                        variant='contained'
                        size='large'
                        onClick={handleAddToContact}
                        disabled={startingChat}
                        startIcon={startingChat && isInNetwork ? <CircularProgress size={20} sx={{ color: '#00C4A7' }} /> : null}
                        sx={{
                           background: 'white',
                           color: '#00C4A7',
                           borderRadius: 3,
                           px: 4,
                           py: 1.5,
                           fontSize: '1rem',
                           fontWeight: 600,
                           boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                           textTransform: 'none',
                           '&:hover': {
                              background: 'rgba(255, 255, 255, 0.95)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
                           },
                           '&:disabled': {
                              background: 'rgba(255, 255, 255, 0.7)',
                              color: '#00C4A7',
                           },
                        }}
                     >
                        {startingChat && isInNetwork ? 'Opening...' : isInNetwork ? 'Start Conversation' : 'Send Network Request'}
                     </Button>
                  )}
               </Box>

               {/* Content Section */}
               <Box sx={{ padding: 4 }}>
                  {/* About Section */}
                  {profile.bio && (
                     <Box sx={{ mb: 4 }}>
                        <Typography
                           variant='h6'
                           sx={{
                              color: '#374151',
                              fontWeight: 600,
                              mb: 2,
                              fontSize: '1.1rem',
                           }}
                        >
                           About
                        </Typography>
                        <Typography
                           sx={{
                              color: '#6b7280',
                              lineHeight: 1.6,
                              fontSize: '0.95rem',
                           }}
                           dangerouslySetInnerHTML={{ __html: profile.bio }}
                        />
                     </Box>
                  )}

                  {/* What I Do Section */}
                  {profile.whatIDo && profile.whatIDo.length > 0 && (
                     <Box sx={{ mb: 4 }}>
                        <Typography
                           variant='h6'
                           sx={{
                              color: '#374151',
                              fontWeight: 600,
                              mb: 2,
                              fontSize: '1.1rem',
                           }}
                        >
                           What I Do
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                           {profile.whatIDo.map((item) => (
                              <Box
                                 key={item.id}
                                 sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    backgroundColor: '#f9fafb',
                                    border: '1px solid #e5e7eb',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                       backgroundColor: '#f3f4f6',
                                       borderColor: '#d1d5db',
                                    },
                                 }}
                              >
                                 <Typography
                                    sx={{
                                       fontSize: '1rem',
                                       fontWeight: 600,
                                       color: '#111827',
                                       mb: 0.5,
                                    }}
                                 >
                                    {item.title}
                                 </Typography>
                                 <Typography
                                    sx={{
                                       fontSize: '0.875rem',
                                       color: '#6b7280',
                                       lineHeight: 1.5,
                                    }}
                                 >
                                    {item.description || item.subtitle}
                                 </Typography>
                              </Box>
                           ))}
                        </Box>
                     </Box>
                  )}

                  {/* Interests & Expertise Section */}
                  {profile.interests && profile.interests.length > 0 && (
                     <Box sx={{ mb: 4 }}>
                        <Typography
                           variant='h6'
                           sx={{
                              color: '#374151',
                              fontWeight: 600,
                              mb: 2,
                              fontSize: '1.1rem',
                           }}
                        >
                           Interests & Expertise
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                           {profile.interests.map((interest) => (
                              <Box
                                 key={interest.id}
                                 sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 2,
                                    py: 1,
                                    borderRadius: 1,
                                    border: '1px solid #e5e7eb',
                                    background: '#f9fafb',
                                 }}
                              >
                                 <Typography sx={{ fontSize: '1rem' }}>{interest.emoji}</Typography>
                                 <Typography
                                    sx={{
                                       fontSize: '0.875rem',
                                       fontWeight: 500,
                                       color: '#374151',
                                    }}
                                 >
                                    {interest.name}
                                 </Typography>
                              </Box>
                           ))}
                        </Box>
                     </Box>
                  )}

                  {/* Looking For Section */}
                  {profile.lookingFor && profile.lookingFor.length > 0 && (
                     <Box sx={{ mb: 4 }}>
                        <Typography
                           variant='h6'
                           sx={{
                              color: '#374151',
                              fontWeight: 600,
                              mb: 2,
                              fontSize: '1.1rem',
                           }}
                        >
                           Looking For
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                           {profile.lookingFor.map((item) => (
                              <Box
                                 key={item.id}
                                 sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    backgroundColor: '#f9fafb',
                                    border: '1px solid #e5e7eb',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                       backgroundColor: '#f3f4f6',
                                       borderColor: '#d1d5db',
                                    },
                                 }}
                              >
                                 <Typography
                                    sx={{
                                       fontSize: '1rem',
                                       fontWeight: 600,
                                       color: '#111827',
                                       mb: 0.5,
                                    }}
                                 >
                                    {item.title}
                                 </Typography>
                                 <Typography
                                    sx={{
                                       fontSize: '0.875rem',
                                       color: '#6b7280',
                                       lineHeight: 1.5,
                                    }}
                                 >
                                    {item.description}
                                 </Typography>
                              </Box>
                           ))}
                        </Box>
                     </Box>
                  )}

                  {/* Featured Section */}
                  {profile.featuredProjects && profile.featuredProjects.length > 0 && (
                     <Box sx={{ mb: 4 }}>
                        <Typography
                           variant='h6'
                           sx={{
                              color: '#374151',
                              fontWeight: 600,
                              mb: 2,
                              fontSize: '1.1rem',
                           }}
                        >
                           Featured
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                           {profile.featuredProjects.map((project) => (
                              <Box
                                 key={project.id}
                                 sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    backgroundColor: '#f9fafb',
                                    border: '1px solid #e5e7eb',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                       backgroundColor: '#f3f4f6',
                                       borderColor: '#d1d5db',
                                    },
                                 }}
                              >
                                 <Typography
                                    sx={{
                                       fontSize: '1rem',
                                       fontWeight: 600,
                                       color: '#111827',
                                       mb: 0.5,
                                    }}
                                 >
                                    {project.title}
                                 </Typography>
                                 <Typography
                                    sx={{
                                       fontSize: '0.875rem',
                                       color: '#6b7280',
                                       lineHeight: 1.5,
                                    }}
                                 >
                                    {project.description}
                                 </Typography>
                              </Box>
                           ))}
                        </Box>
                     </Box>
                  )}

                  {/* Contact Information */}
                  {(profile.email || profile.phoneNumber || profile.website) && (
                     <Box>
                        <Typography
                           variant='h6'
                           sx={{
                              color: '#374151',
                              fontWeight: 600,
                              mb: 2,
                              fontSize: '1.1rem',
                           }}
                        >
                           Contact
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                           {profile.email && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                 <EmailIcon sx={{ color: '#00C4A7', fontSize: 20 }} />
                                 <Typography sx={{ color: '#4b5563', fontSize: '0.9rem' }}>
                                    {profile.email}
                                 </Typography>
                              </Box>
                           )}

                           {profile.phoneNumber && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                 <PhoneIcon sx={{ color: '#00C4A7', fontSize: 20 }} />
                                 <Typography sx={{ color: '#4b5563', fontSize: '0.9rem' }}>
                                    {profile.phoneNumber}
                                 </Typography>
                              </Box>
                           )}

                           {profile.website && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                 <LanguageIcon sx={{ color: '#00C4A7', fontSize: 20 }} />
                                 <Typography sx={{ color: '#4b5563', fontSize: '0.9rem' }}>
                                    <a
                                       href={profile.website}
                                       target='_blank'
                                       rel='noopener noreferrer'
                                       style={{ color: '#00C4A7', textDecoration: 'none' }}
                                    >
                                       {profile.website}
                                    </a>
                                 </Typography>
                              </Box>
                           )}
                        </Box>
                     </Box>
                  )}
               </Box>
            </Paper>
         </Container>

         {/* Signup Modal */}
         <SignupModal
            open={signupModalOpen}
            onClose={() => setSignupModalOpen(false)}
            referrerId={userId}
            source={source}
            onSignupSuccess={handleSignupSuccess}
         />

         {/* Network Request Dialog */}
         <Dialog
            open={showNetworkModal}
            onClose={() => !sendingRequest && setShowNetworkModal(false)}
            maxWidth='sm'
            fullWidth
            PaperProps={{
               sx: { borderRadius: 3 },
            }}
         >
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6' sx={{ fontWeight: 600 }}>
                     Send Network Request
                  </Typography>
                  <IconButton
                     onClick={() => setShowNetworkModal(false)}
                     disabled={sendingRequest}
                     sx={{ color: 'text.secondary' }}
                  >
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent>
               <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant='body2' color='text.secondary'>
                     Send a connection request to <strong>{profile?.displayName}</strong>
                  </Typography>

                  <FormControl fullWidth required>
                     <InputLabel>Relationship Type</InputLabel>
                     <Select
                        value={relationshipType}
                        onChange={(e) => setRelationshipType(e.target.value)}
                        label='Relationship Type'
                        disabled={sendingRequest}
                     >
                        <MenuItem value='friend'>Friend</MenuItem>
                        <MenuItem value='family'>Family</MenuItem>
                        <MenuItem value='colleague'>Colleague</MenuItem>
                        <MenuItem value='business'>Business Contact</MenuItem>
                        <MenuItem value='other'>Other</MenuItem>
                     </Select>
                  </FormControl>

                  <TextField
                     label='Relationship Label (Optional)'
                     value={relationshipLabel}
                     onChange={(e) => setRelationshipLabel(e.target.value)}
                     placeholder='e.g., College Roommate, Work Partner'
                     fullWidth
                     disabled={sendingRequest}
                  />

                  <TextField
                     label='Message (Optional)'
                     value={message}
                     onChange={(e) => setMessage(e.target.value)}
                     placeholder='Add a personal message...'
                     multiline
                     rows={3}
                     fullWidth
                     disabled={sendingRequest}
                  />
               </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
               <Button onClick={() => setShowNetworkModal(false)} disabled={sendingRequest}>
                  Cancel
               </Button>
               <Button
                  onClick={handleNetworkRequestSent}
                  variant='contained'
                  disabled={sendingRequest || !relationshipType}
                  startIcon={sendingRequest ? <CircularProgress size={20} /> : <PersonAddIcon />}
                  sx={{
                     background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                     '&:hover': {
                        background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                     },
                  }}
               >
                  {sendingRequest ? 'Sending...' : 'Send Request'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Snackbar for notifications */}
         <Snackbar
            open={snackbarOpen}
            autoHideDuration={4000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert
               onClose={() => setSnackbarOpen(false)}
               severity={snackbarSeverity}
               sx={{ width: '100%' }}
            >
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </Box>
   );
};

export default QRCodeShare;